import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';

// Variable globale pour suivre si l'API est en cours de chargement
let googleMapsLoading = false;
const loadListeners = [];

// Fonction pour charger Google Maps de manière centralisée
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    // Si déjà chargé, résoudre immédiatement
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('✅ Google Maps déjà chargé depuis HTML');
      resolve();
      return;
    }

    // Vérifier si le script est déjà dans la page (chargé depuis HTML)
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('📡 Script Google Maps trouvé dans le HTML, attente du chargement...');
      
      // Si déjà chargé, résoudre immédiatement
      if (window._googleMapsInitialized && window.google && window.google.maps && window.google.maps.places) {
        console.log('✅ Google Maps déjà disponible');
        resolve();
        return;
      }
      
      // Écouter le callback d'initialisation
      if (window._initGoogleMaps && !window._googleMapsInitialized) {
        const originalCallback = window._initGoogleMaps;
        window._initGoogleMaps = function() {
          originalCallback();
          console.log('✅ Google Maps initialisé via callback');
          resolve();
        };
      }
      
      // Attendre que le script se charge avec un fallback
      let attempts = 0;
      const maxAttempts = 200; // 20 secondes
      
      const checkLoaded = setInterval(() => {
        attempts++;
        if (window._googleMapsInitialized && window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkLoaded);
          console.log('✅ Google Maps chargé depuis le HTML');
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkLoaded);
          console.warn('⚠️ Timeout atteint, mais tentative de continuer...');
          // Accepter même si pas complètement chargé
          if (window.google) {
            console.log('⚠️ Google Maps partiellement chargé, résolution...');
            resolve();
          } else {
            reject(new Error('Timeout: Google Maps n\'a pas pu se charger depuis le HTML'));
          }
        }
      }, 100);
      
      return;
    }

    // Si déjà en cours de chargement, ajouter au listener
    if (googleMapsLoading) {
      loadListeners.push(resolve);
      return;
    }

    // Démarrer le chargement dynamique (fallback si pas dans HTML)
    googleMapsLoading = true;
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAuZ70BPg_cJ01v626EvSEkHlOetRFZGXM';
    
    console.log('📡 Chargement dynamique de Google Maps API...');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      googleMapsLoading = false;
      resolve();
      // Notifier tous les listeners
      loadListeners.forEach(listener => listener());
      loadListeners.length = 0;
    };

    script.onerror = () => {
      googleMapsLoading = false;
      const error = new Error('Erreur lors du chargement de Google Maps API');
      reject(error);
      loadListeners.forEach(listener => listener());
      loadListeners.length = 0;
    };

    document.head.appendChild(script);
  });
};

const MapLocation = ({ 
  value = '', 
  onChange, 
  onLocationSelect,
  placeholder = "Rechercher une adresse...",
  className = "",
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);

  // Charger Google Maps API
  useEffect(() => {
    console.log('🚀 Initialisation MapLocation pour:', placeholder);
    
    loadGoogleMapsAPI()
      .then(() => {
        console.log('✅ Google Maps API chargée, initialisation des services pour:', placeholder);
        initializeGoogleMaps();
      })
      .catch((error) => {
        console.error('❌ Erreur lors du chargement de Google Maps API pour', placeholder, ':', error);
        setIsLoading(false);
      });

    // Pas de nettoyage nécessaire car l'API est partagée
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeGoogleMaps = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('🗺️ Google Maps API initialisée pour MapLocation:', placeholder);
      // TODO: Migrer vers la nouvelle API google.maps.places.Place d'ici mars 2025
      // Documentation: https://developers.google.com/maps/documentation/javascript/places-migration-overview
      // L'ancienne API fonctionne encore et sera supportée au moins 12 mois
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        console.log('✅ Services Google Maps initialisés pour:', placeholder);
      } catch (error) {
        console.error('❌ Erreur lors de l\'initialisation des services Google Maps pour', placeholder, ':', error);
      }
    } else {
      console.error('❌ Google Maps API non disponible pour', placeholder, 'google:', !!window.google, 'maps:', !!window.google?.maps, 'places:', !!window.google?.maps?.places);
    }
  };

  // Rechercher des suggestions d'adresses
  const searchPlaces = async (query) => {
    console.log('🔍 searchPlaces appelé avec:', query, 'autocompleteService:', !!autocompleteService.current);
    if (!query || query.length < 3 || !autocompleteService.current) {
      console.log('⚠️ Conditions non remplies pour searchPlaces');
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    console.log('🔄 Lancement de la recherche Google Maps...');
    setIsLoading(true);

    try {
      // Première tentative : Recherche ciblée en Côte d'Ivoire
      // Utiliser 'geocode' pour obtenir TOUS les types de lieux (résidences, domiciles, entreprises, villes, etc.)
      const primaryRequest = {
        input: query,
        types: ['geocode','establishment'], // ✅ 'geocode' seul inclut TOUS les types de lieux (établissements, adresses, villes, etc.)
        componentRestrictions: { country: 'ci' },
      };

      autocompleteService.current.getPlacePredictions(primaryRequest, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
          const formattedSuggestions = predictions.map(prediction => ({
            id: prediction.place_id,
            address: prediction.description,
            placeId: prediction.place_id,
            structured_formatting: prediction.structured_formatting
          }));
          
          setSuggestions(formattedSuggestions);
          setShowSuggestions(true);
          setIsLoading(false);
        } else {
          // Si aucun résultat en CI, essayer sans restriction de pays
          const fallbackRequest = {
            input: query,
            types: ['geocode','establishment'], // Recherche mondiale avec 'geocode' seul
          };

          autocompleteService.current.getPlacePredictions(fallbackRequest, (fallbackPredictions, fallbackStatus) => {
            setIsLoading(false);
            
            if (fallbackStatus === window.google.maps.places.PlacesServiceStatus.OK && fallbackPredictions) {
              const formattedSuggestions = fallbackPredictions.map(prediction => ({
                id: prediction.place_id,
                address: prediction.description,
                placeId: prediction.place_id,
                structured_formatting: prediction.structured_formatting
              }));
              
              setSuggestions(formattedSuggestions);
              setShowSuggestions(true);
            } else {
              console.warn('⚠️ Aucun résultat trouvé pour:', query);
              setSuggestions([]);
              setShowSuggestions(false);
            }
          });
        }
      });
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresses:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  };

  // Obtenir les détails d'un lieu sélectionné
  const getPlaceDetails = (placeId) => {
    if (!placesService.current) return;

    const request = {
      placeId: placeId,
      fields: ['geometry', 'formatted_address', 'address_components', 'name']
    };

    placesService.current.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const locationData = {
          address: place.formatted_address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          placeId: placeId,
          name: place.name || place.formatted_address
        };

        console.log('📍 Détails du lieu sélectionné:', locationData);
        
        if (onLocationSelect) {
          onLocationSelect(locationData);
        }
      }
    });
  };

  // Géocoder une adresse saisie manuellement
  const geocodeAddress = (address) => {
    if (!window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
        const result = results[0];
        const locationData = {
          address: result.formatted_address,
          latitude: result.geometry.location.lat(),
          longitude: result.geometry.location.lng(),
          placeId: result.place_id,
          name: result.formatted_address
        };

        console.log('📍 Adresse géocodée:', locationData);
        
        if (onLocationSelect) {
          onLocationSelect(locationData);
        }
      } else {
        console.warn('⚠️ Impossible de géocoder l\'adresse:', address);
      }
    });
  };

  // Gérer la saisie de l'utilisateur avec debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setSelectedIndex(-1);
    
    if (onChange) {
      onChange(newValue);
    }

    // Annuler le timer précédent
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Lancer la recherche après 500ms d'inactivité
    if (newValue.length >= 3) {
      debounceTimer.current = setTimeout(() => {
        searchPlaces(newValue);
      }, 500);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Gérer la sélection d'une suggestion
  const handleSuggestionSelect = (suggestion) => {
    setSearchTerm(suggestion.address);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    if (onChange) {
      onChange(suggestion.address);
    }

    getPlaceDetails(suggestion.placeId);
  };

  // Gérer les touches du clavier
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (showSuggestions && selectedIndex >= 0 && selectedIndex < suggestions.length) {
          // Sélectionner une suggestion
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else if (searchTerm.length >= 3) {
          // Géocoder l'adresse saisie manuellement
          geocodeAddress(searchTerm);
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Fermer les suggestions en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target) &&
          suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Synchroniser avec la valeur externe
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Nettoyer le timer debounce lors du démontage
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Liste des suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionSelect(suggestion)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                index === selectedIndex ? 'bg-blue-50 dark:bg-blue-900' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {suggestion.structured_formatting?.main_text || suggestion.address}
                  </div>
                  {suggestion.structured_formatting?.secondary_text && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {suggestion.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message d'aide */}
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        💡 Recherchez n'importe quel lieu : résidences, domiciles, entreprises, magasins... Tapez au moins 3 caractères ou une adresse complète.
      </p>
    </div>
  );
};

export default MapLocation;
